import { VisibilityOptions } from 'config.types'
import { useConfig } from 'domains/config/hooks'
import { updateConfig } from 'domains/config/slice'
import { selectHasError } from 'domains/interrupt/selectors'
import { useAppDispatch } from 'domains/store'
import { useTranslations } from 'domains/translations/hooks'
import { visibilityStates } from 'domains/visibility/constants'
import { useVisibility } from 'domains/visibility/hooks'
import type { VisibilityActionArgs } from 'domains/visibility/visibility.types'
import { useEffect, useRef } from 'preact/hooks'
import { useSelector } from 'react-redux'
import useSeamlyEventBusContext from 'ui/hooks/event-bus-hooks'
import {
  useSeamlyActivityEventHandler,
  useSeamlyApiContext,
  useSeamlyCommands,
  useSeamlyConversationUrl,
  useSeamlyLayoutMode,
  useSeamlyUnreadCount,
} from 'ui/hooks/seamly-hooks'
import { actionTypes, sourceTypes } from 'ui/utils/seamly-utils'

function useSeamlyInstanceFunction(functionName, fn, deps: any[] = []) {
  const eventBus = useSeamlyEventBusContext()

  const callbackRef = useRef(fn)

  useEffect(() => {
    if (!deps.every(Boolean)) {
      return undefined
    }
    const callback = (...args) => callbackRef.current?.(...args)
    // @ts-ignore
    eventBus.emit('function.register', functionName, callback)
    // @ts-ignore
    return () => eventBus.emit('function.unregister', functionName, callback)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventBus, functionName, ...deps])
}

const SeamlyInstanceFunctionsLoader = () => {
  const config = useConfig()
  const { emitEvent, sendAction, sendContext, sendMessage } =
    useSeamlyCommands()
  const dispatch = useAppDispatch()
  const { setVisibility, visible } = useVisibility()
  const currentVisibility = useRef(visible)
  const eventBus = useSeamlyEventBusContext()
  const api = useSeamlyApiContext()
  const unreadCount = useSeamlyUnreadCount()
  const previousUnreadCount = useRef<number | null>(null)
  const previousVisibilityState = useRef<VisibilityOptions>(null)
  const { isInline, isResolving } = useSeamlyLayoutMode()
  const hasError = useSelector(selectHasError)
  const currentConversationUrl = useSeamlyConversationUrl()
  const prevConversationUrl = useRef<string | null>(null)
  const onActivityHandler = useSeamlyActivityEventHandler()
  const { enableTranslations, disableTranslations } = useTranslations()

  useEffect(() => {
    currentVisibility.current = visible
  }, [visible])

  useSeamlyInstanceFunction(
    'askText',
    (text) => {
      sendMessage({ body: text })
    },
    [api.send],
  )

  useSeamlyInstanceFunction(
    'setVariables',
    (variables) => {
      sendContext({ variables })
    },
    [api.send],
  )
  useSeamlyInstanceFunction('getVisibility', (callback) => {
    if (callback) {
      callback(currentVisibility.current)
    } else {
      console.warn(
        'A callback function is required for the getVisibility action.',
      )
    }
  })
  useSeamlyInstanceFunction(
    'setVisibility',
    (args: VisibilityOptions | VisibilityActionArgs) => {
      const visibilityState =
        typeof args === 'string' || args === null ? args : args.visibility

      if (!Object.values(visibilityStates).includes(visibilityState)) {
        console.error(
          'Requested visibility states should be "open", "minimized" ,"hidden" or null.',
        )
        return
      }

      onActivityHandler()
      setVisibility(args)
    },
    [config.api],
  )
  useSeamlyInstanceFunction(
    'sendCustomAction',
    (actionType, body) => {
      const action = { type: 'custom', body: { type: actionType, body } }
      sendAction(action)
      emitEvent(`action.${action.type}`, action)
    },
    [api.send],
  )
  useSeamlyInstanceFunction(
    'setTopic',
    ({ name, fallbackMessage, message, userTriggered }) => {
      if (userTriggered) {
        dispatch(updateConfig({ showSuggestions: false }))
      }

      if (name && fallbackMessage) {
        sendAction({
          type: actionTypes.setTopic,
          body: {
            name,
            fallbackMessage,
            message,
          },
        })
      } else {
        console.warn(
          'A name and a fallbackMessage are required for the setTopic action.',
        )
      }
    },
    [api.send],
  )

  useSeamlyInstanceFunction(
    'setContext',
    (context) => {
      sendContext(context)
    },
    [api.send],
  )

  // Deprecated.
  useSeamlyInstanceFunction(
    'setTranslation',
    ({ enabled, locale }) => {
      if (enabled) {
        enableTranslations(locale, sourceTypes.windowApi)
      } else {
        disableTranslations()
      }
    },
    [api.send],
  )

  useEffect(() => {
    if (!isResolving && !hasError) {
      // Check for app reset
      if (
        prevConversationUrl.current &&
        prevConversationUrl.current !== currentConversationUrl
      ) {
        // @ts-ignore
        eventBus.emit('unreadMessageCount', unreadCount)
      }

      //  Broadcast once on window open
      if (
        visible === visibilityStates.open &&
        visible !== previousVisibilityState.current
      ) {
        if (previousUnreadCount.current !== 0) {
          // @ts-ignore
          eventBus.emit('unreadMessageCount', 0)
        }
      }

      if (
        visible !== visibilityStates.open &&
        unreadCount !== previousUnreadCount.current
      ) {
        // @ts-ignore
        eventBus.emit('unreadMessageCount', unreadCount)
      }

      previousVisibilityState.current = visible
      previousUnreadCount.current = unreadCount
      if (typeof currentConversationUrl === 'string') {
        prevConversationUrl.current = currentConversationUrl
      }
    }
  }, [
    unreadCount,
    visible,
    eventBus,
    isInline,
    isResolving,
    hasError,
    currentConversationUrl,
  ])

  return null
}

export default SeamlyInstanceFunctionsLoader
